/**
 * How a number field reads what is typed into it.
 *
 * Lives here rather than in the app because it is pure, because every number control in the task
 * builder runs through it, and — the reason it moved — because it had no tests. `decimal` was
 * silently stripping the exponent from `6.02e23` and handing back `6.0223`: not a rounding error,
 * twenty-two orders of magnitude, with nothing to catch it. A function with that blast radius
 * belongs where the other pure logic is tested.
 *
 * Three grammars, with sign and domain as separate modifiers rather than more grammars. That is
 * what collapsed nine hand-rolled sanitisers into one table; `filterPositiveNumber` was never a
 * grammar, it was `decimal` with `allowNegative: false`.
 */
/**
 * Keeps free-typed text to what a decimal number can be made of: digits, an optional leading minus,
 * and AT MOST ONE separator. Period and comma both pass — the block parsers accept either, so a
 * pt-BR teacher can type `40,712` and mean what an en-US one writes `40.712`.
 *
 * The "at most one" is the point. A plain character-class strip lets "1.2.3" through, and parseFloat
 * then silently reads it as 1.2 — a malformed tolerance was found persisted in a real block, grading
 * as 1.2 with nothing anywhere saying so.
 */
export function filterDecimalText(value, { allowNegative = false } = {}) {
    const sign = allowNegative && value.startsWith('-') ? '-' : '';
    const body = value.replace(/[^0-9.,]/g, '');
    const firstSep = body.search(/[.,]/);
    if (firstSep === -1)
        return sign + body;
    return sign + body.slice(0, firstSep + 1) + body.slice(firstSep + 1).replace(/[.,]/g, '');
}
/**
 * Strips everything the grammar does not allow, as the teacher types.
 *
 * Three rules are universal here, where before they were applied by whichever block happened to
 * think of them:
 *
 *   - **Both `.` and `,` are decimal separators.** pt-BR writes `1,5`. The variables panel's
 *     scientific branch dropped the comma, so a Brazilian teacher typing `1,5` got `15` — silently,
 *     and only in that one field. Accepting both and normalising on the way out fixes it once.
 *   - **Exactly one separator.** `filterNumber` — the most-used sanitiser, 17 sites across
 *     Numerical, Slider and DataViz — did not enforce this, so `1.2.3` was accepted and
 *     `parseFloat` silently read `1.2`. A real tolerance of `"1.2.3"` was found grading as 1.2.
 *     Angle, Compass and GPS each fixed this locally; the other three never did.
 *   - **The domain never filters typing.** Clamping at the keystroke would make `360` unreachable
 *     if the value passed outside the range on the way there. A domain is checked as an issue.
 */
export function applyGrammar(raw, grammar, allowNegative) {
    if (grammar === 'integer') {
        const negative = allowNegative && raw.trimStart().startsWith('-');
        return (negative ? '-' : '') + raw.replace(/[^0-9]/g, '');
    }
    // The decimal core is `filterDecimalText`, NOT a second implementation of it. That function
    // already got both of the rules right — period and comma both accepted so a pt-BR teacher can
    // type `40,712`, and at most ONE of them, because a plain character-class strip lets `1.2.3`
    // through and parseFloat silently reads 1.2. Writing those rules again here is precisely the
    // duplication this component exists to end; there were already six local copies alongside it.
    if (grammar === 'decimal')
        return filterDecimalText(raw, { allowNegative });
    // Scientific = a decimal mantissa plus an optional exponent. The exponent is split off first so
    // its own sign survives the mantissa's single-separator pass.
    const at = raw.search(/[eE]/);
    if (at === -1)
        return filterDecimalText(raw, { allowNegative });
    const tail = raw.slice(at + 1);
    const exponent = `e${/^\s*-/.test(tail) ? '-' : ''}${tail.replace(/[^0-9]/g, '')}`;
    return filterDecimalText(raw.slice(0, at), { allowNegative }) + exponent;
}
/**
 * The value handed to callers and to the evaluator: one canonical `.` separator.
 *
 * The comma is an input convenience, never a stored form — everything downstream (`parseFloat`,
 * mathjs, the evaluators in `packages/blocks`) speaks `.` only.
 */
export function normalizeNumber(value) {
    return value.replace(',', '.');
}
//# sourceMappingURL=number-grammar.js.map